import { motion } from "framer-motion";
import {
  Eye,
  AlertCircle,
  MessageCircleQuestion,
  GitBranch,
  Lightbulb,
} from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const FEATURES = [
  {
    icon: Eye,
    title: "What You Might Notice",
    text: "Signs and situations that may indicate something in your books needs attention.",
  },
  {
    icon: AlertCircle,
    title: "Why It Matters",
    text: "An explanation of why the issue can affect your bookkeeping and your business.",
  },
  {
    icon: MessageCircleQuestion,
    title: "Questions to Ask",
    text: "Questions to help you investigate what is happening and what information you may need.",
  },
  {
    icon: GitBranch,
    title: "Common Causes",
    text: "Examples of what may have caused the bookkeeping issue in the first place.",
  },
  {
    icon: Lightbulb,
    title: "Professional Insight",
    text: "Additional perspective to help you better understand the issue and what deserves your attention.",
  },
];

export const InsideBook = () => (
  <section id="inside" data-testid="inside-section" className="relative bg-white py-20 sm:py-28">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: EASE }}
        className="mx-auto max-w-2xl text-center"
      >
        <div className="mb-5 flex items-center justify-center gap-3">
          <span className="h-px w-10 bg-[#C5A253]" />
          <span className="text-[12px] font-bold uppercase tracking-[0.24em] text-[#14A8A3]">
            Inside the Book
          </span>
          <span className="h-px w-10 bg-[#C5A253]" />
        </div>
        <h2
          data-testid="inside-heading"
          className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#1E2A2B]"
        >
          What You&rsquo;ll Find Inside
        </h2>
        <p
          data-testid="inside-subheading"
          className="mt-5 text-base md:text-lg leading-relaxed text-[#1E2A2B]/65"
        >
          A practical resource designed to help business owners recognize
          bookkeeping problems and understand what deserves their attention.
        </p>
      </motion.div>

      <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5">
        {FEATURES.map((f, i) => (
          <motion.div
            key={f.title}
            data-testid={`feature-item-${i + 1}`}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: i * 0.1, ease: EASE }}
            className={`group relative px-8 py-14 lg:py-16 text-center lg:text-left ${
              i > 0 ? "lg:border-l lg:border-[#1E2A2B]/8" : ""
            } sm:max-lg:border-b sm:max-lg:border-[#1E2A2B]/6 hover:bg-[#F6F5F0] transition-colors duration-500`}
          >
            <span className="font-display text-base italic text-[#C5A253]">
              {String(i + 1).padStart(2, "0")}
            </span>
            <div className="mt-6 flex justify-center lg:justify-start">
              <div className="flex h-20 w-20 items-center justify-center rounded-full border border-[#20C9C3]/40 bg-[#20C9C3]/8 transition-[background-color,border-color,transform] duration-500 group-hover:bg-[#20C9C3] group-hover:border-[#20C9C3] group-hover:scale-110">
                <f.icon
                  data-testid={`feature-icon-${i + 1}`}
                  className="h-9 w-9 text-[#14A8A3] transition-colors duration-500 group-hover:text-white"
                  strokeWidth={1.6}
                />
              </div>
            </div>
            <h3 className="mt-7 font-display text-xl xl:text-2xl font-semibold leading-snug text-[#1E2A2B]">
              {f.title}
            </h3>
            <p className="mt-4 text-[15px] leading-relaxed text-[#1E2A2B]/60">
              {f.text}
            </p>
            <span className="absolute bottom-0 left-1/2 lg:left-8 h-[2px] w-0 -translate-x-1/2 lg:translate-x-0 bg-[#20C9C3] transition-[width] duration-500 group-hover:w-12" />
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
